// biome-ignore-all lint/suspicious/noExplicitAny: pre-existing generic select wrapper; option/value/event/renderValue types are intentionally loose and retyping ripples across 160+ call sites (tracked separately).
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  type FormControlProps,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  type SxProps,
  type Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { Drawer } from "components/drawer";
import {
  CheckCircleIcon,
  CloseIcon,
  DownArrowIcon,
  InfoIcon,
} from "components/icons";
import React, {
  type ElementRef,
  forwardRef,
  type Ref,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller } from "react-hook-form";
import {
  convertStringToSentenceCase,
  useGetCurrentBreakpoint,
} from "utilz/helpers";
import { Input, type InputProps } from "./Input";
import { LabelWithInfo } from "./LabelWithInfo";

export interface CustomSelectFieldProps extends Omit<FormControlProps, "size"> {
  selectedItemWithStyle?: boolean;
  options?: Array<any>;
  label?: string;
  /** Optional help text shown via an info (i) icon next to the label. */
  tooltip?: React.ReactNode;
  name: string;
  control?: any;
  onValueChange?: (e: any) => void;
  //   FIXME: make this required
  labelMappingKey?: string;
  valueMappingKey?: string;
  value?: any;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  size?: "small" | "medium" | "large";
  isResponsive?: boolean;
  hideHelperBox?: boolean;
  isGrouped?: boolean;
  groupedOptions?: Record<string, Array<any>>;
  renderValue?: (value: any, optionValue?: any) => React.ReactNode;
  menuItemBuilder?: ({
    item,
    index,
    isSelected,
  }: {
    item: any;
    index: number;
    isSelected?: boolean;
  }) => React.ReactNode;
  open?: boolean;
  inputRef?: Ref<HTMLElement>;
  onOpen?: (...event: any) => void;
  onClose?: (...event: any) => void;
  isSearchable?: boolean;
  onSearchValueChange?: (value: string) => void;
  /** Fired when the searchable menu scrolls near its bottom (infinite load). */
  onMenuScrollEnd?: () => void;
  searchFieldProps?: Omit<InputProps, "name">;
  /** Custom client-side filter; return true for all to defer to server search. */
  onFilterValue?: (value: any) => boolean;
  additionalPaperComponent?: React.ReactNode;
  isClearable?: boolean;
  isLoading?: boolean;
  isDrawer?: boolean;
  noOptionText?: string;
  footerComponent?: React.ReactNode;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  endAdornment?: React.ReactNode;
  maxWidthOfOptions?: string;
  /**
   * fn used as callback after onChange of React Hook Forms is run
   *
   */
  customRhfOnChange?: (value: any) => void;
  keepMenuItemOriginalCasing?: boolean;
  /**
   * Drawer mode only. When set, the drawer gets a Cancel / confirm footer and
   * the row tapped is staged until confirmed, instead of committing at once.
   */
  drawerConfirmLabel?: string;
  /** Drawer mode only. Heading for the drawer; falls back to `placeholder`. */
  drawerTitle?: string;
  /** Drawer mode only. Width of the drawer body; defaults to 400px. */
  drawerWidth?: string;
  /**
   * Accessible name for a select shown without a visible `label` (e.g. inline
   * "The [First] [Monday]" pickers). Ignored when absent.
   */
  ariaLabel?: string;
}
export const SelectField = ({
  options,
  label,
  tooltip,
  size = "small",
  name,
  control,
  onChange,
  onValueChange,
  labelMappingKey,
  valueMappingKey,
  error,
  errorMessage,
  value,
  required = false,
  isResponsive = false,
  hideHelperBox = false,
  menuItemBuilder,
  open,
  isGrouped = false,
  groupedOptions = {},
  onClose,
  isSearchable = false,
  onSearchValueChange,
  onMenuScrollEnd,
  searchFieldProps,
  onFilterValue,
  additionalPaperComponent,
  renderValue,
  placeholder,
  isClearable = false,
  defaultValue,
  customRhfOnChange,
  isDrawer = false,
  noOptionText = "No options",
  sx,
  isLoading = false,
  footerComponent,
  selectedItemWithStyle,
  autoFocus,
  endAdornment,
  onOpen,
  maxWidthOfOptions,
  keepMenuItemOriginalCasing,
  drawerConfirmLabel,
  drawerTitle,
  drawerWidth,
  ariaLabel,
  ...rest
}: CustomSelectFieldProps) => {
  return (
    <React.Fragment>
      <FormControl fullWidth {...rest}>
        {control ? (
          <Controller
            control={control}
            name={name}
            render={({ field: { ref, value, onBlur, onChange, name } }) => (
              <CustomSelectField
                ref={ref}
                name={name}
                error={error}
                value={value}
                label={label}
                tooltip={tooltip}
                onBlur={onBlur}
                onOpen={onOpen}
                onClose={onClose}
                onChange={(e) => {
                  customRhfOnChange &&
                    customRhfOnChange(isDrawer ? e : e?.target?.value);
                  onChange(e);
                }}
                {...(open !== undefined ? { open } : {})}
                // onValueChange={onValueChange}
                isResponsive={isResponsive}
                size={size}
                options={options}
                valueMappingKey={valueMappingKey}
                labelMappingKey={labelMappingKey}
                required={required}
                type="controlled"
                menuItemBuilder={menuItemBuilder}
                isGrouped={isGrouped}
                isLoading={isLoading}
                groupedOptions={groupedOptions}
                isSearchable={isSearchable}
                onSearchValueChange={onSearchValueChange}
                onMenuScrollEnd={onMenuScrollEnd}
                onFilterValue={onFilterValue}
                searchFieldProps={searchFieldProps}
                additionalPaperComponent={additionalPaperComponent}
                footerComponent={footerComponent}
                renderValue={
                  isGrouped || renderValue
                    ? renderValue
                    : (value, optionValue) => (
                        <>
                          {optionValue
                            ? keepMenuItemOriginalCasing
                              ? optionValue[`${labelMappingKey}`]
                              : convertStringToSentenceCase(
                                  optionValue[`${labelMappingKey}`],
                                )
                            : typeof defaultValue === "string"
                              ? keepMenuItemOriginalCasing
                                ? defaultValue
                                : convertStringToSentenceCase(defaultValue)
                              : defaultValue}
                        </>
                      )
                }
                placeholder={placeholder}
                isClearable={isClearable}
                isDrawer={isDrawer}
                noOptionText={noOptionText}
                sx={sx}
                autoFocus={autoFocus}
                endAdornment={endAdornment}
                maxWidthOfOptions={maxWidthOfOptions}
                variant={rest?.variant}
                keepMenuItemOriginalCasing={keepMenuItemOriginalCasing}
                drawerConfirmLabel={drawerConfirmLabel}
                drawerTitle={drawerTitle}
                drawerWidth={drawerWidth}
                ariaLabel={ariaLabel}
                defaultValue={defaultValue}
              />
            )}
          />
        ) : (
          <CustomSelectField
            name={name}
            error={error}
            value={value}
            label={label}
            tooltip={tooltip}
            onValueChange={onValueChange}
            onOpen={onOpen}
            onClose={onClose}
            isResponsive={isResponsive}
            size={size}
            options={options}
            open={open}
            isLoading={isLoading}
            valueMappingKey={valueMappingKey}
            labelMappingKey={labelMappingKey}
            required={required}
            type="uncontrolled"
            menuItemBuilder={menuItemBuilder}
            isGrouped={isGrouped}
            groupedOptions={groupedOptions}
            isSearchable={isSearchable}
            onSearchValueChange={onSearchValueChange}
            onMenuScrollEnd={onMenuScrollEnd}
            onFilterValue={onFilterValue}
            searchFieldProps={searchFieldProps}
            additionalPaperComponent={additionalPaperComponent}
            footerComponent={footerComponent}
            renderValue={
              isGrouped || renderValue
                ? renderValue
                : (value, optionValue) => (
                    <>
                      {optionValue
                        ? keepMenuItemOriginalCasing
                          ? optionValue[`${labelMappingKey}`]
                          : convertStringToSentenceCase(
                              optionValue[`${labelMappingKey}`],
                            )
                        : typeof defaultValue === "string"
                          ? keepMenuItemOriginalCasing
                            ? defaultValue
                            : convertStringToSentenceCase(defaultValue)
                          : defaultValue}
                    </>
                  )
            }
            placeholder={placeholder}
            isClearable={isClearable}
            isDrawer={isDrawer}
            noOptionText={noOptionText}
            sx={sx}
            selectedItemWithStyle={selectedItemWithStyle}
            autoFocus={autoFocus}
            endAdornment={endAdornment}
            maxWidthOfOptions={maxWidthOfOptions}
            variant={rest?.variant}
            keepMenuItemOriginalCasing={keepMenuItemOriginalCasing}
            drawerConfirmLabel={drawerConfirmLabel}
            drawerTitle={drawerTitle}
            drawerWidth={drawerWidth}
            defaultValue={defaultValue}
            ariaLabel={ariaLabel}
          />
        )}

        <Box
          sx={{
            pl: "14px",
            mt: errorMessage ? "4px" : hideHelperBox ? "0px" : "4px",
          }}
        >
          <Typography variant="body-small" color="error">
            {errorMessage}
          </Typography>
        </Box>
      </FormControl>
    </React.Fragment>
  );
};

interface Props extends Omit<CustomSelectFieldProps, "onChange"> {
  onChange?: (...event: any) => void;
  type: "controlled" | "uncontrolled";
  isGrouped?: boolean;
  groupedOptions?: Record<string, Array<any>>;
  isSearchable?: boolean;
  onSearchValueChange?: (value: string) => void;
  /** Fired when the searchable menu scrolls near its bottom (infinite load). */
  onMenuScrollEnd?: () => void;
  searchFieldProps?: Omit<InputProps, "name">;
  onFilterValue?: (value: any) => boolean;
  isClearable?: boolean;
  endAdornment?: React.ReactNode;
}
const CustomSelectField = ({
  ref,
  error,
  value,
  label,
  tooltip,
  onValueChange,
  isResponsive,
  size,
  options,
  valueMappingKey,
  labelMappingKey,
  onChange,
  required,
  control,
  type,
  open,
  renderValue,
  menuItemBuilder,
  isGrouped = false,
  groupedOptions = {},
  onClose,
  isSearchable = false,
  onSearchValueChange,
  onMenuScrollEnd,
  searchFieldProps,
  onFilterValue,
  additionalPaperComponent,
  footerComponent,
  placeholder,
  isClearable = true,
  isLoading = false,
  isDrawer,
  inputRef,
  noOptionText,
  sx,
  onOpen,
  selectedItemWithStyle,
  autoFocus,
  endAdornment,
  maxWidthOfOptions,
  variant,
  keepMenuItemOriginalCasing,
  drawerConfirmLabel,
  drawerTitle,
  drawerWidth = "400px",
  defaultValue,
  ariaLabel,
}: Props) => {
  const theme = useTheme();
  // Drawer footer mode stages the tapped row until the user confirms.
  const [stagedValue, setStagedValue] = useState<any>(undefined);
  // biome-ignore lint/correctness/useHookAtTopLevel: pre-existing conditional ref init; refactor tracked separately (out of scope for the tooltip change).
  const selectRef = inputRef || useRef<HTMLElement>(null);
  const searchInputRef = useRef<ElementRef<"input">>(null);
  const breakpoint = useGetCurrentBreakpoint(theme);
  const [searchValue, setSearchValue] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    if (isMenuOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isMenuOpen]);

  if (isResponsive) {
    if (breakpoint === "lg") {
      size = "large";
    } else if (breakpoint === "md") {
      size = "medium";
    } else {
      size = "small";
    }
  }

  const getStyle = () => {
    switch (size) {
      case "small":
        return {
          height: "32px",
        };
      case "medium":
        return { height: "40px" };
      case "large":
        return { height: "48px" };
      default:
        return { height: "48px" };
    }
  };

  useEffect(() => {
    onSearchValueChange?.(searchValue);
  }, [searchValue]);

  // Drawer mode: clear the search when the drawer closes, as the dropdown's
  // onClose already does. The drawer closes from several places (outside
  // click, picking an item, Cancel / confirm). With a server-side search
  // (`onSearchValueChange`) a stale term would filter the next field's list.
  useEffect(() => {
    if (isDrawer && !isMenuOpen) setSearchValue("");
  }, [isDrawer, isMenuOpen]);

  // Default client-side search. Matches the primary label key plus the common
  // name fields, so a user can find an option by first, middle or last name
  // regardless of which field is used as the label (e.g. label is first_name
  // but the search term is a last name). An empty term matches everything.
  const matchesSearch = (option: Record<string, any>) => {
    const term = searchValue.toLocaleLowerCase();
    if (!term) return true;
    return [
      labelMappingKey,
      "first_name",
      "middle_name",
      "last_name",
      "email",
    ].some(
      (key) =>
        key &&
        option?.[key] &&
        `${option[key]}`.toLocaleLowerCase().includes(term),
    );
  };

  // `open` covers a parent that opens the dropdown itself (MUI doesn't call
  // onOpen then).
  const isListOpen = isMenuOpen || (!isDrawer && Boolean(open));

  // A closed list shows nothing, so skip filtering until it opens.
  const filteredOption = isListOpen
    ? isSearchable
      ? options?.filter(onFilterValue ? onFilterValue : matchesSearch)
      : options
    : [];

  // Items for the MUI Select's own menu. While the dropdown is closed — and
  // always in drawer mode, where the drawer renders the list — MUI only needs
  // the selected option to match and display the value. Building every option
  // here meant rows × options items on every render in tables.
  const onlySelectedInMenu = isDrawer || !isListOpen;
  const selectedMenuOption = onlySelectedInMenu
    ? options?.find((option) => option?.[valueMappingKey || ""] === value)
    : undefined;
  const menuOptions = onlySelectedInMenu
    ? selectedMenuOption
      ? [selectedMenuOption]
      : []
    : filteredOption;

  const groupedOptionsArr = Object.entries(groupedOptions);
  const filteredGroupedOptions = isSearchable
    ? groupedOptionsArr
        .map((group) => [group[0], group[1].filter(matchesSearch)])
        .filter((group) => group[1].length > 0)
    : groupedOptionsArr;

  // MUI floats the label from the input's own "filled" state, which misses
  // two cases: a value set while the field was uncontrolled (MUI fixes that on
  // first render — e.g. a drawer pick on a field that started null), and an
  // option whose value is "" (e.g. "No Template"), which MUI counts as empty.
  // If the value resolves to a shown option, the label must float.
  const hasSelectedOption =
    !isGrouped &&
    value !== undefined &&
    value !== null &&
    Boolean(
      options?.some((option) => option?.[valueMappingKey || ""] === value),
    );
  const shrinkLabel = defaultValue || hasSelectedOption ? true : undefined;

  return (
    <>
      <InputLabel
        shrink={shrinkLabel}
        sx={{
          borderRadius: "4px",

          color: theme.palette.lightBg.low,
          mr: "4px",
          fontFamily: theme.typography["body-medium"].fontFamily,
          fontWeight: theme.typography["body-medium"].fontWeight,
          fontSize: theme.typography["body-medium"].fontSize,
          transform: {
            xs: "translate(14px,6px) scale(1)",
          },
          "&.MuiFormLabel-filled, &.MuiInputLabel-shrink": {
            transform:
              variant === "standard"
                ? "translate(14px,6px) scale(.0)"
                : "translate(14px, -9px) scale(.75)",
            backgroundColor: theme.palette.common.white,
            padding: "0 4px",
            marginLeft: "-4px",
          },
          "&.Mui-focused": {
            color: theme.palette.purple.main,
            transform:
              variant === "standard"
                ? "translate(14px,6px) scale(.0)"
                : "translate(14px, -9px) scale(.75)",
            backgroundColor: theme.palette.common.white,
            padding: "0 4px",
            marginLeft: "-4px",
          },
        }}
      >
        {label && (
          <LabelWithInfo
            label={label}
            required={required}
            error={error}
            tooltip={tooltip}
          />
        )}
      </InputLabel>
      {/* @ts-ignore */}
      <Select
        variant={variant}
        placeholder={
          placeholder === "string"
            ? convertStringToSentenceCase(placeholder)
            : placeholder
        }
        ref={ref}
        autoFocus={autoFocus}
        inputRef={selectRef}
        error={error}
        value={value}
        // The notched-outline legend is sized from this `label`. Default
        // (no tooltip): pass the original plain string — byte-identical to the
        // previous behaviour so existing selects' notch is unchanged. Only when
        // a tooltip is set do we widen the legend to reserve room for the (i)
        // icon. MUI renders the legend at font-size: 0.75em, so the node must
        // NOT force a fixed font size (an em-scaled icon keeps the notch tight).
        label={
          label ? (
            tooltip ? (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "2px",
                }}
              >
                {convertStringToSentenceCase(label)}
                {required ? " *" : ""}
                <InfoIcon sx={{ fontSize: "1.1em" }} />
              </Box>
            ) : (
              convertStringToSentenceCase(label)
            )
          ) : (
            ""
          )
        }
        open={isDrawer ? false : open}
        // Every MUI input injects its own global autofill keyframes, which
        // only detect browser autofill. A select is never autofilled, and in a
        // table each copy is a separate <style> insert at the top of <head>
        // (500 rows took ~8 s). Skip them.
        disableInjectingGlobalStyles
        // Keep the outline's gap in step with a forced-floating label.
        {...(shrinkLabel && (!variant || variant === "outlined")
          ? { notched: true }
          : {})}
        onClose={() => {
          setSearchValue("");
          setIsMenuOpen(false);
          onClose?.();
        }}
        onOpen={(e) => {
          setIsMenuOpen(true);
          onOpen?.();
        }}
        displayEmpty
        // MUI names the display by `aria-labelledby` (pointing at itself, as
        // `name` is set), which beats `aria-label` — clear it so ours wins.
        SelectDisplayProps={
          ariaLabel
            ? { "aria-label": ariaLabel, "aria-labelledby": undefined }
            : {}
        }
        IconComponent={(e) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            {endAdornment && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "16px",
                }}
              >
                {endAdornment}
              </Box>
            )}
            <IconComponent
              value={value}
              onClear={() => {
                type === "controlled"
                  ? onChange && onChange(null)
                  : onValueChange && onValueChange(null);
              }}
              isClearable={isClearable}
              className={e.className}
            />
          </Box>

          // <IconComponent
          //   value={value}
          //   onClear={() => {
          //     type === "controlled"
          //       ? onChange && onChange(null)
          //       : onValueChange && onValueChange(null);
          //   }}
          //   isClearable={isClearable}
          //   className={e.className}
          // />
        )}
        // endAdornment={
        //   <IconComponent
        //     value={value}
        //     onClick={() => {
        //       console.log(selectRef.current);
        //       if (selectRef.current) {
        //         console.log("herwe");
        //         selectRef.current.focus(); // Focus the Select component
        //         // selectRef.current.click(); // Trigger the click to open the dropdown
        //       }
        //     }}
        //     onClear={() => {
        //       type === "controlled"
        //         ? onChange && onChange(null)
        //         : onValueChange && onValueChange(null);
        //     }}
        //     isClearable={isClearable}
        //   />
        // }
        // // IconComponent={IconComponent}
        // inputProps={{
        //   IconComponent: () => null,
        // }}
        {...(renderValue
          ? {
              renderValue: (selected) => {
                const selectedOption = options?.find(
                  (option) => option?.[valueMappingKey || ""] === selected,
                );
                return (
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {renderValue(selected, selectedOption)}
                  </Box>
                );
              },
            }
          : isSearchable
            ? {
                renderValue: (selected) => {
                  const groupedOptionsMergedArr = groupedOptionsArr?.reduce(
                    // @ts-expect-error
                    (accumulatedArr, currentGroup) => {
                      return [...accumulatedArr, ...currentGroup[1]];
                    },
                    [],
                  );
                  // const newOptions = isSearchable
                  //   ? groupedOptionsMergedArr
                  //   : options;
                  const newOptions = isGrouped
                    ? groupedOptionsMergedArr
                    : options;
                  const selectedOption = newOptions?.find(
                    (option) => option?.[valueMappingKey || ""] === selected,
                  );
                  return (
                    <Typography variant="body-medium">
                      {selectedOption?.[labelMappingKey || ""] || ""}
                    </Typography>
                  );
                },
              }
            : {})}
        onChange={(e) => {
          // A pinned row (additionalPaperComponent, e.g. SelectAddOption) is a
          // MenuItem with no value; MUI still reports the click as a change.
          // It's an action, not a choice — don't clear the field for it.
          if (e.target.value === undefined) return;
          type === "controlled"
            ? onChange && onChange(e)
            : onValueChange && onValueChange(e.target.value);
        }}
        sx={{
          // textTransform: "capitalize",
          ...theme.typography["body-medium"],
          "& .MuiSelect-select": {
            borderRadius: "4px",
            py: 0,
          },

          "&.MuiInputBase-root": {
            minWidth: "110px",
            pr: "4px !important",
            backgroundColor: error ? theme.palette.error.tint : "white",
            borderBottomWidth: "1px",

            "&.MuiInput-underline": {
              mt: "0px",

              backgroundColor: "transparent",
              "&:before": {
                borderBottomColor: error
                  ? theme.palette.error.main
                  : theme.palette.neutral["020"],
                borderBottomWidth: "1px",
              },
              "&:hover:not(.Mui-disabled):before": {
                borderBottomColor: error
                  ? theme.palette.error.main
                  : theme.palette.lightBg.medium,
                borderBottomWidth: "1px",
              },
              "&:after": {
                borderBottomColor: theme.palette.purple.main,
                borderBottomWidth: "2px",
              },
            },
            ...getStyle(),
          },

          borderRadius: "4px",
          ...(sx ? sx : {}),
          // "& .MuiSelect-icon": {
          //   display: "none",
          //   color: theme.palette.lightBg.main,
          // },
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              margin: "16px 0px",
              padding: "0px",
              overflow: "scroll",
              maxHeight: "300px",
              // width: "200px",
              maxWidth: "500px",
              backgroundColor: "#FFFFFF",
              borderRadius: "4px",
              boxShadow: "8px 8px 32px rgba(0, 0, 0, 0.07)",
              overflowX: "clip",
            },
          },
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "left",
          },
          transformOrigin: {
            vertical: "top",
            horizontal: "left",
          },
          sx: {
            "& .MuiList-root": {
              p: "0px",
              pb: footerComponent ? "0px" : "8px",
            },
            "& .MuiMenuItem-root, & .MuiListSubheader-root": {
              maxWidth: maxWidthOfOptions || "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& .MuiMenuItem-root .MuiTypography-root": {
              minWidth: 0,
              flexGrow: 1,
            },
          },
          disableAutoFocusItem: true,
        }}
      >
        {isSearchable && (
          <Box
            width={"100%"}
            position={"sticky"}
            bgcolor={"white"}
            top={"0px"}
            left={"0px"}
            zIndex={"2"}
            p={"4px"}
            borderBottom={`1px solid ${theme.palette.neutral["015"]}`}
            onKeyDownCapture={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(ev) => {
              ev.stopPropagation();
            }}
          >
            <Input
              inputRef={searchInputRef}
              name="select_search"
              fullWidth
              size="small"
              placeholder="Search"
              isResponsive={false}
              hideHelperBox
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onChange={(event: any) => {
                event.stopPropagation();
                event.preventDefault();
                setSearchValue(event.target.value);
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.neutral["005"],

                  "& > fieldset": {
                    border: "none",
                    ...theme.typography["body-small"],
                  },
                },
              }}
              {...searchFieldProps}
            />
          </Box>
        )}
        {additionalPaperComponent && additionalPaperComponent}

        {isLoading ? (
          <MenuItem
            disabled
            sx={{
              padding: "12px 16px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <CircularProgress size={"20px"} />
          </MenuItem>
        ) : isGrouped ? (
          groupedOptions &&
          (filteredGroupedOptions?.length > 0 ? (
            filteredGroupedOptions.flatMap((data, groupIndex) => {
              if (!(data && Array.isArray(data))) {
                return [];
              }
              return data.flatMap((value, valueIndex) => {
                if (value && typeof value === "string") {
                  return [
                    <ListSubheader
                      key={`options_sub_${groupIndex}_${valueIndex}`}
                      sx={{
                        ...theme.typography["caption"],
                        padding: "12px 16px",
                        backgroundColor: theme.palette.neutral["010"],
                      }}
                    >
                      {keepMenuItemOriginalCasing
                        ? value
                        : convertStringToSentenceCase(value)}
                    </ListSubheader>,
                  ];
                } else if (value && Array.isArray(value)) {
                  return value.map((item: any, itemIndex) => (
                    <MenuItem
                      key={`options_${groupIndex}_${valueIndex}_${itemIndex}`}
                      value={item[`${valueMappingKey}`]}
                      sx={{
                        ...theme.typography["body-small"],
                        display: "flex",
                        alignItems: "center",
                        borderBottom: `${
                          itemIndex === (value?.length || 0) - 1
                            ? "0px"
                            : `1px solid ${theme.palette.neutral["010"]}`
                        }`,
                      }}
                    >
                      <Typography
                        variant="body-small"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                          minWidth: 0,
                          flexGrow: 1,
                        }}
                      >
                        {keepMenuItemOriginalCasing
                          ? item?.[`${labelMappingKey}`] || ""
                          : convertStringToSentenceCase(
                              item?.[`${labelMappingKey}`],
                            )}
                      </Typography>
                    </MenuItem>
                  ));
                }
                return [];
              });
            })
          ) : (
            <MenuItem
              disabled
              sx={{
                ...theme.typography["body-medium"],
                padding: "12px 16px",
              }}
            >
              {noOptionText}
            </MenuItem>
          ))
        ) : (Array.isArray(menuOptions) ? menuOptions : []).length <= 0 ? (
          <MenuItem
            disabled
            sx={{
              ...theme.typography["body-medium"],
              padding: "12px 16px",
            }}
          >
            {noOptionText}
          </MenuItem>
        ) : (
          (Array.isArray(menuOptions) ? menuOptions : []).map((item, index) => (
            <MenuItem
              key={`options_${index}`}
              value={item[`${valueMappingKey}`]}
              disabled={item?.disabled}
              sx={{
                ...theme.typography["body-small"],
                display: "flex",
                alignItems: "center",
                // textTransform: "capitalize",
                padding: "12px 16px",
                wordWrap: "break-word",
                borderBottom: `${
                  index === (options?.length || 0) - 1
                    ? "0px"
                    : `1px solid ${theme.palette.neutral["010"]}`
                }`,
                ...(selectedItemWithStyle && {
                  "&.Mui-selected": {
                    backgroundColor: theme.palette.primitive.brand.purple[25],
                    "&:hover": {
                      backgroundColor: theme.palette.primitive.brand.purple[25],
                    },
                  },
                }),
              }}
            >
              {(menuItemBuilder &&
                menuItemBuilder({
                  item,
                  index,
                  isSelected: value === item[`${valueMappingKey}`],
                })) || (
                <Typography
                  variant="body-small"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    minWidth: 0,
                    flexGrow: 1,
                  }}
                >
                  {item[`${labelMappingKey}`]
                    ? keepMenuItemOriginalCasing
                      ? item[`${labelMappingKey}`]
                      : convertStringToSentenceCase(item[`${labelMappingKey}`])
                    : ""}
                </Typography>
              )}
            </MenuItem>
          ))
        )}
        {footerComponent && footerComponent}
      </Select>
      {isDrawer && (
        <Drawer
          open={isMenuOpen}
          // SwipeableDrawer keeps a closed drawer mounted by default. Each
          // drawer holds the whole option list, so in a table (one field per
          // row) that mounted thousands of hidden items. Mount it only while
          // open; the search and picked value live in this component.
          ModalProps={{ keepMounted: false }}
          // The swipe gesture can't open this drawer (open is controlled by
          // the field click), but it still mounts an invisible fixed strip
          // per field. Turn it off.
          disableSwipeToOpen
          title={drawerTitle || placeholder}
          onClose={() => {
            setStagedValue(undefined);
            setIsMenuOpen(false);
          }}
          {...(drawerConfirmLabel
            ? {
                secondaryButtonText: "Cancel",
                onSecondaryButtonClick: () => {
                  setStagedValue(undefined);
                  setIsMenuOpen(false);
                },
                primaryButtonText: drawerConfirmLabel,
                isPrimaryButtonDisabled: stagedValue === undefined,
                onPrimaryButtonClick: () => {
                  type === "controlled"
                    ? onChange && onChange(stagedValue)
                    : onValueChange && onValueChange(stagedValue);
                  setStagedValue(undefined);
                  setIsMenuOpen(false);
                },
              }
            : { footer: <></> })}
        >
          <Box
            sx={{
              width: drawerWidth,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              height: "100%",
            }}
          >
            <Box>
              {isSearchable && (
                <Box
                  width={"100%"}
                  position={"sticky"}
                  bgcolor={"white"}
                  p={"12px 24px"}
                  borderBottom={`1px solid ${theme.palette.neutral["015"]}`}
                  onKeyDownCapture={(event) => {
                    event.stopPropagation();
                  }}
                  onKeyDown={(ev) => {
                    ev.stopPropagation();
                  }}
                >
                  <Input
                    inputRef={searchInputRef}
                    name="select_search"
                    fullWidth
                    size="small"
                    placeholder="Search"
                    isResponsive={false}
                    hideHelperBox
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                    onChange={(event: any) => {
                      event.stopPropagation();
                      event.preventDefault();
                      setSearchValue(event.target.value);
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: theme.palette.neutral["005"],

                        "& > fieldset": {
                          border: "none",
                          ...theme.typography["body-small"],
                        },
                      },
                    }}
                    {...searchFieldProps}
                  />
                </Box>
              )}
              {/* Pinned above the list, as in the menu — e.g. "+ Tax". */}
              {additionalPaperComponent}
            </Box>
            <Stack
              onScroll={(e) => {
                if (!onMenuScrollEnd) return;
                const el = e.currentTarget;
                // Within 24px of the bottom → ask the parent for the next page.
                if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
                  onMenuScrollEnd();
                }
              }}
              sx={{
                flex: 1,
                overflow: "scroll",
                // Every row is at least 48px whatever its text style; taller
                // content (a second line) still grows it.
                "& .MuiMenuItem-root": {
                  minHeight: "48px",
                },
              }}
            >
              {isLoading ? (
                <MenuItem
                  disabled
                  sx={{
                    padding: "12px 16px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "fit-content",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                >
                  <CircularProgress size={"20px"} />
                </MenuItem>
              ) : !filteredOption || filteredOption?.length === 0 ? (
                <MenuItem
                  disabled
                  sx={{
                    ...theme.typography["body-medium"],
                    padding: "14px 24px",
                    minHeight: "fit-content",
                    // justifyContent: "center",
                  }}
                >
                  {noOptionText}
                </MenuItem>
              ) : (
                (Array.isArray(filteredOption) ? filteredOption : []).map(
                  (item, index) => (
                    <MenuItem
                      onClick={(e) => {
                        if (drawerConfirmLabel) {
                          setStagedValue(item[`${valueMappingKey}`]);
                          return;
                        }
                        type === "controlled"
                          ? onChange && onChange(item[`${valueMappingKey}`])
                          : onValueChange &&
                            onValueChange(item[`${valueMappingKey}`]);

                        setIsMenuOpen(false);
                      }}
                      key={`options_${index}`}
                      value={item[`${valueMappingKey}`]}
                      sx={{
                        ...theme.typography["body-medium"],
                        minHeight: "fit-content",
                        // width: "300px",
                        textTransform: "capitalize",
                        padding: "12px 24px",
                        // Space between the row's value and the tick column.
                        gap: "24px",
                        wordWrap: "break-word",
                        justifyContent: "space-between",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        backgroundColor:
                          (drawerConfirmLabel && stagedValue !== undefined
                            ? stagedValue
                            : value) === item[`${valueMappingKey}`]
                            ? theme.palette.primitive.brand.purple[25]
                            : "white",
                      }}
                    >
                      {/* min-width 0 lets the content shrink, so a long name truncates
                          instead of pushing the value into the tick column. */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {(menuItemBuilder &&
                          menuItemBuilder({
                            item,
                            index,
                            isSelected:
                              (drawerConfirmLabel && stagedValue !== undefined
                                ? stagedValue
                                : value) === item[`${valueMappingKey}`],
                          })) || (
                          <Typography
                            variant="body-medium"
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                            textOverflow={"ellipsis"}
                          >
                            {item[`${labelMappingKey}`]
                              ? keepMenuItemOriginalCasing
                                ? item[`${labelMappingKey}`]
                                : convertStringToSentenceCase(
                                    item[`${labelMappingKey}`],
                                  )
                              : ""}
                          </Typography>
                        )}
                      </Box>
                      <Box
                        sx={{ width: "24px", height: "24px", flexShrink: 0 }}
                      >
                        {(drawerConfirmLabel && stagedValue !== undefined
                          ? stagedValue
                          : value) === item[`${valueMappingKey}`] && (
                          <CheckCircleIcon
                            sx={{
                              color: theme.palette.primitive.brand.purple[700],
                            }}
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ),
                )
              )}
            </Stack>
          </Box>
        </Drawer>
      )}
    </>
  );
};

// biome-ignore lint/correctness/noUnusedVariables: pre-existing unused default builder; preserved for reference, cleanup tracked separately.
const DefaultMenuBuilder = ({
  item,
  index,
  valueMappingKey,
  labelMappingKey,
  options,
  keepMenuItemOriginalCasing,
}: {
  item: any;
  index: number;
  valueMappingKey?: string;
  labelMappingKey?: string;
  options?: Array<any>;
  keepMenuItemOriginalCasing?: boolean;
}) => {
  const theme = useTheme();
  return (
    <MenuItem
      value={item[`${valueMappingKey}`]}
      sx={{
        ...theme.typography["body-small"],
        display: "flex",
        alignItems: "center",
        textTransform: "capitalize",
        padding: "12px 16px",
        wordWrap: "break-word",
        borderBottom: `${
          index === (options?.length || 0) - 1
            ? "0px"
            : `1px solid ${theme.palette.neutral["010"]}`
        }`,
      }}
    >
      <Typography
        variant="body-small"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          maxWidth: "100%",
          minWidth: 0,
          flexGrow: 1,
        }}
      >
        {item[`${labelMappingKey}`]
          ? keepMenuItemOriginalCasing
            ? item[`${labelMappingKey}`]
            : convertStringToSentenceCase(item[`${labelMappingKey}`])
          : ""}
      </Typography>
    </MenuItem>
  );
};

// biome-ignore lint/correctness/noUnusedVariables: pre-existing unused grouped-items renderer; preserved for reference, cleanup tracked separately.
const GroupedItems = forwardRef(
  (
    {
      title,
      valueMappingKey,
      labelMappingKey,
      options,
      keepMenuItemOriginalCasing,
      ...props
    }: any,
    ref: any,
  ) => {
    const theme = useTheme();
    return (
      <>
        <ListSubheader
          sx={{
            ...theme.typography["caption"],
            textTransform: "capitalize",
            padding: "12px 16px",
            backgroundColor: theme.palette.neutral["010"],
          }}
        >
          {title}
        </ListSubheader>
        {options && Array.isArray(options)
          ? options.map((item: any, index) => (
              <MenuItem
                key={`options_${index}`}
                ref={ref}
                {...props}
                value={item[`${valueMappingKey}`]}
                sx={{
                  ...theme.typography["body-small"],
                  display: "flex",
                  alignItems: "center",
                  borderBottom: `${
                    index === (options?.length || 0) - 1
                      ? "0px"
                      : `1px solid ${theme.palette.neutral["010"]}`
                  }`,
                }}
              >
                <Typography
                  variant="body-small"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    minWidth: 0,
                    flexGrow: 1,
                  }}
                >
                  {item[`${labelMappingKey}`]
                    ? keepMenuItemOriginalCasing
                      ? item[`${labelMappingKey}`]
                      : convertStringToSentenceCase(item[`${labelMappingKey}`])
                    : ""}
                </Typography>
              </MenuItem>
            ))
          : ""}
      </>
    );
  },
);

const IconComponent = ({
  value,
  onClear,
  isClearable = true,
  onClick,
  className,
}: {
  value: any;
  onClear?: () => void;
  isClearable: boolean;
  onClick?: () => void;
  className: any;
}) => {
  const theme = useTheme();
  return (
    <Stack
      direction={"row"}
      divider={<Divider orientation="vertical" flexItem />}
      gap={"2px"}
      alignItems={"center"}
    >
      {isClearable && value ? (
        <CloseIcon
          sx={{
            cursor: "pointer",
            width: 18,
            height: 18,
            marginRight: "4px",
          }}
          onClick={onClear}
          htmlColor={theme.palette.lightBg.low}
          fontSize="small"
          className="MuiSelect-icon MuiSelect-iconOutlined"
          // className={className}
        />
      ) : (
        <DownArrowIcon
          fontSize="small"
          sx={{ cursor: "pointer" }}
          htmlColor={theme.palette.lightBg.low}
          onClick={onClick}
          className={className}
        />
      )}
    </Stack>
  );
};
